
// src/components/CustomDatePicker.js
import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { FormControl, FormLabel, FormHelperText } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const CustomDatePicker = ({ label, value, onChange, minDate, maxDate, name, error, helperText, infoText, required = true, disabled, noMarginTop = false, labelMarginBottom = '' }) => {
  const [open, setOpen] = useState(false);
  /** After dismiss (outside click / Escape / icon), MUI restores focus to the input — skip one focus so the picker does not immediately reopen. */
  const skipNextFocusOpenRef = useRef(false);

  const beginClosePicker = () => {
    skipNextFocusOpenRef.current = true;
    setOpen(false);
    globalThis.setTimeout(() => {
      skipNextFocusOpenRef.current = false;
    }, 300);
  };

  const handlePickerClose = () => {
    beginClosePicker();
  };

  const handlePickerOpen = () => {
    if (!disabled) setOpen(true);
  };

  const handleFieldClick = () => {
    if (disabled) return;
    if (open) {
      beginClosePicker();
      return;
    }
    setOpen(true);
  };

  let helperContent = null;
  if (error && helperText) {
    helperContent = (
      <FormHelperText error sx={{ marginLeft: 0, marginTop: "4px", fontSize: "12px" }}>
        {helperText}
      </FormHelperText>
    );
  } else if (infoText) {
    helperContent = (
      <FormHelperText sx={{ marginLeft: 0, marginTop: "4px", fontSize: "12px", color: "#6B7280" }}>
        {infoText}
      </FormHelperText>
    );
  }

  return (
    <FormControl fullWidth>
      <FormLabel
        className="form-label"
        htmlFor={name}
        sx={{
          color: "#1C252E",
          fontSize: {
            xs: "13px",
            sm: "13px",
            md: "14px",
            xl: "14px",
          },
          fontWeight: "500",
          lineHeight: "22px",
          mb: labelMarginBottom || "0px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div>
          {label} {required && <span className="asteriskColor"> *</span>}
        </div>
      </FormLabel>
      <DatePicker
        format="DD/MM/YYYY"
        open={open}
        onOpen={handlePickerOpen}
        onClose={handlePickerClose}
        value={value ? dayjs(value) : null}
        onChange={(newValue) => {
          onChange({
            target: {
              name,
              value: newValue ? newValue.format("YYYY-MM-DD") : "",
            },
          });
        }}
        minDate={minDate ? dayjs(minDate) : null}
        maxDate={maxDate ? dayjs(maxDate) : null}
        slotProps={{
          textField: {
            fullWidth: true,
            variant: "outlined",
            error: error,
            onClick: handleFieldClick,

            onMouseDown: (e) => {
              e.preventDefault();
            },

            InputProps: {
              readOnly: true,
              onKeyDown: (e) => {
                if (e.key === "Tab" || e.key === "Escape") return;
                if (
                  e.key === "Enter" ||
                  e.key === " " ||
                  e.key === "ArrowDown"
                ) {
                  e.preventDefault();
                  if (!disabled) setOpen(true);
                  return;
                }
                e.preventDefault();
              },
            },
            inputProps: {
              readOnly: true,

              onKeyDown: (e) => {
                e.preventDefault();
              },

              style: {
                cursor: disabled
                  ? "default"
                  : "pointer",

                userSelect: "none", // prevent selection
              },
            },
            sx: {
              marginTop: noMarginTop ? "0" : "5px",
              "& .MuiOutlinedInput-root": {
                cursor: disabled ? "default" : "pointer",
                height: "44px",
                borderRadius: "8px",
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                fontSize: "14px",
                backgroundColor: "inherit",
                ...(error && {
                  borderColor: "#d32f2f",
                  "& fieldset": {
                    borderColor: "#d32f2f",
                  },
                  "&:hover fieldset": {
                    borderColor: "#d32f2f",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#d32f2f",
                  },
                }),
              },
              "& .MuiInputBase-input": {
                cursor: disabled ? "default" : "pointer",
                height: "100%",
                fontSize: "14px",
              },
              "& .MuiIconButton-root": {
                cursor: disabled ? "default" : "pointer",
              },
              "& .MuiInputBase-input::placeholder": {
                textTransform: "initial",
                fontSize: "14px",
                color: "#4F5E71",
                opacity: 1,
              },
            },
          },
        }}
        disabled={disabled}
      />
      {helperContent}
    </FormControl>
  );
};



CustomDatePicker.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.any,
  helperText: PropTypes.any,
  infoText: PropTypes.any,
  label: PropTypes.any,
  labelMarginBottom: PropTypes.any,
  maxDate: PropTypes.any,
  minDate: PropTypes.any,
  name: PropTypes.any,
  noMarginTop: PropTypes.any,
  onChange: PropTypes.func,
  required: PropTypes.any,
  value: PropTypes.any,
};

export default CustomDatePicker;